import firebase from './firebase';

/**
 * Creates a public group from filtered users and filter state
 * Removes sensitive information from user data
 * Sets an expiration date of 14 days from creation
 * 
 * @param {Array} filteredUsers - Array of filtered user objects
 * @param {Object} filterState - Current filter state
 * @returns {Promise<string>} - ID of the created public group
 */
const createPublicGroup = async (filteredUsers, filterState) => {
  try {
    // Make sure filteredUsers is an array
    if (!filteredUsers || !Array.isArray(filteredUsers)) {
      console.error('filteredUsers is not an array:', filteredUsers);
      filteredUsers = []; // Default to empty array to avoid further errors
    }
    
    // Make sure filterState is an object
    if (!filterState || typeof filterState !== 'object') {
      console.error('filterState is not an object:', filterState);
      filterState = {}; // Default to empty object
    }
    
    console.log('Original filterState:', JSON.stringify(filterState, (key, value) => 
      value === undefined ? "UNDEFINED_VALUE" : value
    ));
    // Calculate expiration date (14 days from now)
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 14);
    
    // Sanitize user data - remove sensitive information
    const sanitizedUsers = filteredUsers.map(user => {
      // Build name safely
      let displayName = '';
      if (user.name) {
        displayName = user.name;
      } else if (user.firstName || user.lastName) {
        displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      } else {
        displayName = 'Freelancer'; // Fallback name
      }

      // Build title safely
      let displayTitle = '';
      if (user.title) {
        displayTitle = user.title;
      } else if (user.roles && Array.isArray(user.roles) && user.roles.length > 0 && user.roles[0].name) {
        displayTitle = user.roles[0].name;
      }

      // Process roles data to match filter requirements
      let processedRoles = [];
      
      // If user has roles array with proper format, use it
      if (Array.isArray(user.roles) && user.roles.length > 0) {
        processedRoles = user.roles.map(role => {
          // If role is already a properly formatted object
          if (typeof role === 'object' && role !== null) {
            return {
              experience: role.experience || 0,
              industry: role.industry || '',
              industryId: role.industryId || '',
              label: role.label || role.name || '',
              roleId: role.roleId || role.id || '',
              skills: Array.isArray(role.skills) ? role.skills : [],
              yearStarted: role.yearStarted || 0,
              name: role.name || ''
            };
          }
          // If role is just a string
          return {
            experience: 0,
            industry: '',
            industryId: '',
            label: typeof role === 'string' ? role : '',
            roleId: '',
            skills: [],
            yearStarted: 0,
            name: typeof role === 'string' ? role : ''
          };
        });
      }
      // If user has titles and experienceByRole, create roles from those
      else if (Array.isArray(user.titles) && user.titles.length > 0 && user.experienceByRole) {
        processedRoles = user.titles.map(title => {
          const experience = user.experienceByRole[title] || 0;
          
          // Get any skills that might be available from the user object
          let roleSkills = [];
          
          // If user has a skills array, include it for all roles
          if (Array.isArray(user.skills) && user.skills.length > 0) {
            roleSkills = user.skills;
          }
          
          return {
            experience: experience,
            industry: '',
            industryId: '',
            label: title,
            roleId: '',
            skills: roleSkills,
            yearStarted: new Date().getFullYear() - experience,
            name: title
          };
        });
      }

      return {
        id: user.id || '',
        name: displayName,
        avatar: user.avatar || null,
        roles: processedRoles,
        // Also include original roles for backward compatibility
        originalRoles: Array.isArray(user.roles) ? user.roles : [],
        titles: Array.isArray(user.titles) ? user.titles : [],
        experienceByRole: user.experienceByRole || {},
        // Include skills directly in user object
        skills: Array.isArray(user.skills) ? user.skills : [],
        title: displayTitle,
        city: user.city || '',
        union: user.unionStatus || '',
        province: user.province || '',
        diversityTags: Array.isArray(user.diversityTags) ? user.diversityTags : [],
        availability: user.latestAvail || null,
        industries: Array.isArray(user.industries) ? user.industries : [],
        // Include market data if available for city/industry context
        market: user.market || null,
        // Exclude sensitive information like email and phone number
      };
    });

    // More robust sanitization function for Firestore
    const sanitizeForFirestore = (data) => {
      // Base cases
      if (data === undefined) return null;
      if (data === null) return null;
      if (typeof data !== 'object') return data;
      
      // Handle Date objects
      if (data instanceof Date) {
        return firebase.firestore.Timestamp.fromDate(data);
      }
      
      // Handle Arrays
      if (Array.isArray(data)) {
        const sanitizedArray = [];
        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          if (item !== undefined) {
            sanitizedArray.push(sanitizeForFirestore(item));
          } else {
            sanitizedArray.push(null);
          }
        }
        return sanitizedArray;
      }
      
      // Handle objects
      const sanitizedObj = {};
      for (const [key, value] of Object.entries(data)) {
        // Skip undefined values
        if (value === undefined) {
          sanitizedObj[key] = null;
        } else {
          sanitizedObj[key] = sanitizeForFirestore(value);
        }
      }
      
      return sanitizedObj;
    };
    
    // Create sanitized copy of filterState
    const sanitizedFilterState = sanitizeForFirestore(filterState);
    
    // Log sanitized object to verify it has no undefined values
    console.log('Sanitized filterState:', JSON.stringify(sanitizedFilterState));
    
    // Double check for undefined values with more detailed logging
    const checkForUndefined = (obj, path = '') => {
      if (obj === undefined) {
        console.error(`Found undefined at path: ${path}`);
        return true;
      }
      if (obj === null || typeof obj !== 'object') return false;
      
      if (Array.isArray(obj)) {
        return obj.some((item, index) => checkForUndefined(item, `${path}[${index}]`));
      }
      
      return Object.entries(obj).some(([key, value]) => {
        const newPath = path ? `${path}.${key}` : key;
        return checkForUndefined(value, newPath);
      });
    };
    
    const hasUndefined = checkForUndefined(sanitizedFilterState);
    if (hasUndefined) {
      console.error('Warning: Sanitized object still contains undefined values!');
    }

    // Create final document to store - with a last safety check
    const docData = {
      users: sanitizedUsers || [],
      filterState: sanitizedFilterState || {},
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      createdBy: firebase.auth().currentUser?.uid || null, // Handle null case
      expiresAt: firebase.firestore.Timestamp.fromDate(expirationDate),
      userCount: Array.isArray(filteredUsers) ? filteredUsers.length : 0,
    };
    
    // Final check for any undefined values
    const finalSanitizedData = sanitizeForFirestore(docData);
    console.log('Final document to store:', finalSanitizedData);
    
    // Create document in publicGroups collection
    const docRef = await firebase.firestore().collection('publicGroups').add(finalSanitizedData);

    return {
      id: docRef.id,
      expirationDate
    };
  } catch (error) {
    console.error('Error creating public group:', error);
    throw error;
  }
};

export default createPublicGroup;
